import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CheckCircle, FilePenLine, FilePlus2, Trash2, MessageSquare, Clock, User, Phone, MapPin } from 'lucide-react';
import ModalEditarAviso from '../componentes/ModalEditarAviso';
import ModalVerImagenes from '../componentes/ModalVerImagenes';
import { getEstadoBadge } from '../utilidades/estadoUtils';
import { API_URL } from '../servicios/api';

const AvisosCompletados = () => {
  const navigate = useNavigate();
  const [avisos, setAvisos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [avisoSeleccionado, setAvisoSeleccionado] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [tecnicoGlobalId, setTecnicoGlobalId] = useState(localStorage.getItem('filtro_tecnico_id') || '');
  const [isImagenesModalOpen, setIsImagenesModalOpen] = useState(false);
  const [imagenesSeleccionadas, setImagenesSeleccionadas] = useState([]);

  const getWhatsAppPhone = (phone) => {
    const digits = String(phone ?? '').replace(/\D/g, '');
    if (digits.startsWith('34') && digits.length === 11) return digits;
    if (digits.length === 9 && /^[6789]/.test(digits)) return `34${digits}`;
    return digits.length >= 10 && digits.length <= 15 ? digits : '';
  };

  const handleCreateInvoice = (aviso) => {
    navigate('/facturacion', {
      state: {
        borradorFactura: {
          idAviso: Number(aviso.id_aviso),
          idCliente: Number(aviso.id_cliente_fk ?? aviso.cliente_id),
          nombreCliente: aviso.nombre_cliente ?? '',
          descripcion: aviso.descripcion ?? '',
          diagnostico: aviso.observaciones_tecnico ?? '',
          precioCobrado: aviso.precio_cobrado
        }
      }
    });
  };

  // Función auxiliar para formatear nombres de documentos
  const formatDocumentName = (docName) => {
    switch (docName) {
      case 'factura': return 'Factura';
      case 'albaran': return 'Albarán';
      case 'informe_tecnico': return 'Informe Técnico';
      case 'presupuesto': return 'Presupuesto';
      default: return docName.charAt(0).toUpperCase() + docName.slice(1).replace(/_/g, ' ');
    }
  };

  const obtenerAvisos = useCallback(async () => {
    setCargando(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No autenticado. Por favor, inicia sesión.');
      setCargando(false);
      return;
    }

    try {
      // Fetch only completed notices, without a limit
      const params = new URLSearchParams({ estado: 'completado,documentos_enviados,resuelto' });
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      const usuarioActual = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (usuarioActual?.rol === 'administrador' && tecnicoGlobalId) params.set('tecnico_id', tecnicoGlobalId);
      const url = `${API_URL}/obtener_avisos.php?${params.toString()}`;

      const respuesta = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const datos = await respuesta.json();

      // El backend ahora devuelve directamente el array de avisos, no un objeto con 'exito'
      const avisosRecibidos = datos.exito && Array.isArray(datos.avisos) ? datos.avisos : []; // Asegurarse de que sea un array

      const avisosConImagenes = await Promise.all(avisosRecibidos.map(async (aviso) => {
        const imagenesRes = await fetch(`${API_URL}/obtener_imagenes_aviso.php?id_aviso=${aviso.id_aviso}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        const imagenesData = await imagenesRes.json();
        return { ...aviso, imagenes: imagenesData.exito ? imagenesData.imagenes : [] };
      }));
      setAvisos(avisosConImagenes);
      // No hay 'else' aquí porque el backend devuelve un array vacío si no hay avisos
      if (avisosRecibidos.length === 0) {
        setError('No se encontraron avisos completados.'); // O un mensaje más específico si el backend lo envía
      } else {
        setError(null); // Clear any previous error
      }
    } catch (err) {
      console.error('Error al obtener avisos completados:', err);
      setError('Error de conexión con el servidor.');
      setAvisos([]); // Clear previous data
    } finally {
      setCargando(false);
    }
  }, [fechaDesde, fechaHasta, tecnicoGlobalId]);

  useEffect(() => {
    obtenerAvisos();
  }, [obtenerAvisos]);

  useEffect(() => {
    const onTecnicoGlobalChange = (event) => setTecnicoGlobalId(event.detail?.tecnicoId || '');
    window.addEventListener('filtro-tecnico-cambiado', onTecnicoGlobalChange);
    return () => window.removeEventListener('filtro-tecnico-cambiado', onTecnicoGlobalChange);
  }, []);

  const handleEditClick = (aviso) => {
    setAvisoSeleccionado(aviso);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAvisoSeleccionado(null);
  };

  const handleAvisoUpdated = () => {
    obtenerAvisos();
  };

  const handleDeleteClick = async (id_aviso) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este aviso? Esta acción no se puede deshacer.')) {
      return;
    }
    // ... (delete logic remains the same)
  };

  const handleVerImagenesClick = (imagenes) => {
    setImagenesSeleccionadas(imagenes);
    setIsImagenesModalOpen(true);
  };

  const handleCloseImagenesModal = () => {
    setIsImagenesModalOpen(false);
    setImagenesSeleccionadas([]);
  };

  const renderTableBody = () => {
    if (cargando) {
      return <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">Cargando...</td></tr>;
    }
    if (error && avisos.length === 0) {
      return <tr><td colSpan="5" className="px-4 py-10 text-center text-red-400">Error: {error}</td></tr>;
    }
    if (avisos.length === 0) {
      return <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No se encontraron avisos completados.</td></tr>;
    }
    return avisos.map((aviso) => (
      <tr key={aviso.id_aviso} className="bg-slate-800 hover:bg-slate-700 transition-colors rounded-lg">
        <td className="px-4 py-4 align-top rounded-l-lg">
          <div className="font-medium text-slate-100">{aviso.nombre_cliente}</div>
          <div className="text-sm text-slate-400 truncate max-w-xs">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              {aviso.direccion}
            </a>
          </div>
        </td>
        <td className="px-4 py-4 align-top">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <a href={`tel:${aviso.telefono}`} className="hover:text-blue-400 transition-colors">{aviso.telefono}</a>
            <a href={`https://wa.me/${(aviso.telefono ?? '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 transition-colors"><MessageSquare className="w-4 h-4" /></a>
          </div>
          <div className="text-sm text-blue-400 hover:text-blue-300 truncate max-w-xs">
            <a href={`mailto:${aviso.email_cliente}`}>{aviso.email_cliente}</a>
          </div>
        </td>
        <td className="px-4 py-4 align-top">
          <div className="text-sm text-slate-300">
            {new Date(aviso.fecha_completado).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            {' - '}
            {new Date(aviso.fecha_completado).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-sm text-slate-400 truncate max-w-xs">{aviso.descripcion}</div>
        </td>
        <td className="px-4 py-4 align-top">{getEstadoBadge(aviso.estado)}</td>
        <td className="px-4 py-4 align-top rounded-r-lg">
          <div className="flex items-center gap-2">
            <button className="p-1.5 text-blue-400 hover:text-white hover:bg-blue-700 rounded-md transition-colors" onClick={() => handleVerImagenesClick(aviso.imagenes)}><Camera className="w-5 h-5" /></button>
            <button className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-md transition-colors" onClick={() => handleEditClick(aviso)}><FilePenLine className="w-5 h-5" /></button>
            <button className="p-1.5 text-red-400 hover:text-white hover:bg-red-700 rounded-md transition-colors" onClick={() => handleDeleteClick(aviso.id_aviso)}><Trash2 className="w-5 h-5" /></button>
          </div>
        </td>
      </tr>
    ));
  };

  const renderCardView = () => {
    // Función auxiliar para formatear nombres de documentos
    const formatDocumentName = (docName) => {
      switch (docName) {
        case 'factura': return 'Factura';
        case 'albaran': return 'Albarán';
        case 'informe_tecnico': return 'Informe Técnico';
        case 'presupuesto': return 'Presupuesto';
        default: return docName.charAt(0).toUpperCase() + docName.slice(1).replace(/_/g, ' ');
      }
    };

    if (cargando) {
      return <p className="text-slate-400 text-center mt-4">Cargando...</p>;
    }
    if (error && avisos.length === 0) {
      return <p className="text-red-400 text-center mt-4">Error: {error}</p>;
    }
    if (avisos.length === 0) {
      return <p className="text-slate-400 text-center mt-4">No se encontraron avisos completados.</p>;
    }
    return avisos.map((aviso) => (
      <div key={aviso.id_aviso} className="h-full bg-slate-800 rounded-lg shadow-md p-3 sm:p-4 border border-slate-700 hover:border-blue-500/50 transition-all duration-300">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
          <div className="flex items-center gap-2 mb-2 sm:mb-0 flex-wrap">
            <span className="font-bold text-base text-blue-400">#{aviso.numero_aviso ?? aviso.id_aviso}</span>
            {getEstadoBadge(aviso.estado)}
            <span className="text-[11px] font-medium text-slate-400 bg-slate-700 px-2 py-1 rounded-full">Prioridad: {aviso.prioridad}</span>
          </div>
          <div className="flex flex-col sm:items-end gap-0.5 text-xs text-white">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-3.5 h-3.5 text-white" />
              <span className="text-slate-300">Completado: {new Date(aviso.fecha_completado).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            {aviso.fecha_creacion && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                <span>Creado: {new Date(aviso.fecha_creacion).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <User className="w-3.5 h-3.5 text-white shrink-0" />
              <span className="font-medium text-slate-200 break-words">{aviso.nombre_cliente}</span>
            </div>
            {aviso.nombre_empresa && (
              <div className="flex items-center gap-2 mb-1 text-xs">
                <span className="text-slate-400">Empresa:</span>
                <span className="font-medium text-slate-200 break-words">{aviso.nombre_empresa}</span>
              </div>
            )}
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-3.5 h-3.5 text-white shrink-0" />
              <a href={`tel:${aviso.telefono}`} className="text-blue-400 hover:underline">
                {aviso.telefono}
              </a>
              {getWhatsAppPhone(aviso.telefono) && (
                <a
                  href={`https://wa.me/${getWhatsAppPhone(aviso.telefono)}?text=${encodeURIComponent(
                    `Hola ${aviso.nombre_cliente || ''}, tu reparación del aviso #${aviso.numero_aviso ?? aviso.id_aviso} ha finalizado.${aviso.descripcion ? ` ${aviso.descripcion}` : ''}${aviso.observaciones_tecnico ? ` Diagnóstico: ${aviso.observaciones_tecnico}` : ''}`
                  )}`}
                  className="text-green-400 hover:text-green-300 ml-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Enviar resumen del aviso ${aviso.id_aviso} por WhatsApp`}
                  title="Enviar resumen de reparación por WhatsApp"
                >
                  <MessageSquare className="w-4 h-4" />
                </a>
              )}
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-white mt-0.5 shrink-0" />
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(aviso.direccion)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline text-xs break-words"
              >
                {aviso.direccion}
              </a>
            </div>
          </div>
          
          <div className="sm:text-right">
            <div className="mb-1">
              <span className="text-slate-400">Técnico:</span>
              <span className="ml-2 font-medium text-slate-200">{aviso.nombre_tecnico || 'No asignado'}</span>
            </div>
            <div>
              <span className="text-slate-400">Coste:</span>
              <span className="ml-2 font-bold text-green-400">{parseFloat(aviso.precio_cobrado || 0).toFixed(2)}€</span>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/50 rounded-lg p-2.5 mb-3">
          <div className="mb-1">
            <span className="font-medium text-slate-300">Descripción:</span>
            <p className="text-xs text-slate-400 mt-0.5 break-words [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden" title={aviso.descripcion}>{aviso.descripcion}</p>
          </div>
          {aviso.observaciones_tecnico && (
            <div className="mt-1">
              <span className="font-medium text-slate-300">Diagnóstico / Observaciones:</span>
              <p className="text-xs text-slate-400 mt-0.5 break-words [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden" title={aviso.observaciones_tecnico}>{aviso.observaciones_tecnico}</p>
            </div>
          )}

        {aviso.documentos_a_enviar && JSON.parse(aviso.documentos_a_enviar).length > 0 && (
          <div className="mt-2">
            <span className="font-medium text-slate-300">Documentos a Enviar:</span>
            <ul className="list-disc list-inside text-sm text-slate-400 mt-1">
              {JSON.parse(aviso.documentos_a_enviar).map((doc, index) => (
                <li key={index}>{formatDocumentName(doc)}</li>
              ))}
            </ul>
          </div>
        )}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2">
          <div className="flex gap-1.5 flex-wrap justify-end">
            <button
              onClick={() => handleCreateInvoice(aviso)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs transition-colors"
            >
              <FilePlus2 className="w-4 h-4" />
              Crear factura
            </button>
            <button 
              onClick={() => handleVerImagenesClick(aviso.imagenes)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs transition-colors"
            >
              <Camera className="w-4 h-4" />
              Imágenes
            </button>
            <button 
              onClick={() => handleEditClick(aviso)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-xs transition-colors"
            >
              <FilePenLine className="w-4 h-4" />
              Editar
            </button>
            <button 
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs transition-colors"
              onClick={() => handleDeleteClick(aviso.id_aviso)}
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Avisos Completados</h1>
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <label htmlFor="fechaDesde" className="text-sm font-medium text-gray-700">Desde:</label>
          <input type="date" id="fechaDesde" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto" />
          <label htmlFor="fechaHasta" className="text-sm font-medium text-gray-700">Hasta:</label>
          <input type="date" id="fechaHasta" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto" />
          <button onClick={obtenerAvisos} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Filtrar</button>
        </div>
      </div>

      {/* Cards remain stacked on mobile and use a compact grid on wide screens. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-stretch">
        {renderCardView()}
      </div>

      {avisoSeleccionado && (
        <ModalEditarAviso
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          aviso={avisoSeleccionado}
          onUpdateAviso={handleAvisoUpdated}
        />
      )}

      <ModalVerImagenes
        isOpen={isImagenesModalOpen}
        onClose={handleCloseImagenesModal}
        imagenes={imagenesSeleccionadas}
      />
    </div>
  );
};

export default AvisosCompletados;
